import { useState } from 'react';

import { Bandeira } from './Bandeira.js';
import { api } from '../lib/api.js';
import { useRetratoDoIdioma, useT } from '../lib/idioma.js';
import {
  aoTrocarALinguaDaArea,
  guardarPreferencia,
  preferenciasGuardadas,
} from '../lib/preferencias-do-player.js';

/**
 * ── O SELETOR DE IDIOMA DO ALUNO ───────────────────────────────────────────
 *
 * Ordem do dono (22/10/2026): *«estando disponivel mais de uma lingua, apesar
 * de selecionado pelo nativo do aluno, ele pode mudar para outra (…) **se ele
 * altera, essa é que vale**»*.
 *
 * ## Ele vive no CABEÇALHO, e não na tela da conta
 *
 * `/perfil` é tela de **pessoa**, não de área — está escrito no próprio
 * ficheiro, e é por isso que ele não recebe `area`. Mas as línguas que se
 * podem escolher são da ÁREA: numa tela sem área não há lista que mostrar.
 * O cabeçalho é a única superfície que tem as duas coisas.
 *
 * ## Ele LÊ o contexto, e não pede nada
 *
 * A primeira escrita fazia o seu próprio `GET`, e o provedor fazia outro: dois
 * pedidos da mesma coisa em toda tela da área. Quem o apanhou foi a cerca
 * `os-links-do-topo-chegam-a-toda-tela`, que conta os pedidos do topo — ver
 * `lib/idioma.tsx`.
 *
 * ## Ele apaga-se sozinho
 *
 * `null` do servidor quer dizer *«esta área é monolingue»*, que é o estado de
 * todas até o tutor habilitar a primeira língua. O mesmo vale com uma língua
 * só: um seletor com uma opção é um controle que promete escolha e não tem
 * nenhuma (a `R6` desta casa).
 *
 * ## A BANDEIRA, e não o nome (24/10/2026)
 *
 * Ordem do dono: *«eu quero que na area de membros fique a bandeira da lingua
 * pra escolher. e nao o nome»*.
 *
 * Isso **tirou o `<select>`**, e não por gosto: um `<select>` nativo só sabe
 * desenhar texto — a lista dele é do sistema operativo, e nenhum `<option>`
 * aceita um `<svg>` dentro. Mantê-lo com a bandeira por cima daria a bandeira
 * fechado e a lista de NOMES aberta, que é metade da ordem.
 *
 * ## Por que uma FILEIRA, e não um menu
 *
 * Um menu custaria dois gestos (abrir, escolher) e um estado a mais para
 * fechar, tudo para esconder — no caso real — duas ou três bandeiras. Em
 * fileira, trocar de língua é **um toque**, e as línguas que a área tem ficam
 * à vista em vez de atrás de uma porta. O componente já se apaga sozinho
 * abaixo de duas, logo a fileira nunca tem um item só.
 *
 * A largura não é a do `ALVO` (44px) porque ali o alvo embrulha uma PALAVRA, e
 * `Sair` tem quatro letras; aqui ele embrulha uma figura de 24px de largura
 * fixa. A **altura de 44px fica**, que é o que a cerca `C2` do cabeçalho mede
 * — e ela mede-a em todo `header a, header button`, inclusive nestes.
 *
 * O `flex-wrap` não é enfeite: na gaveta do telefone a linha tem 224px e
 * sobram-lhe ~192 depois do `px-3`, enquanto seis bandeiras com folga pedem
 * 224. Sem ele, a sexta sai pela borda — e sai **calada**, porque nada numa
 * cerca de DOM mede largura (o `happy-dom` não faz layout, e está escrito no
 * cabeçalho de `Cabecalho.test.tsx`).
 *
 * ## Por que a página recarrega depois de gravar
 *
 * Porque a língua muda **tudo** — a interface, o catálogo e a semente da faixa
 * do player, que o servidor resolve. Um `setState` repintaria o seletor e
 * deixaria a página à volta na língua antiga, que é a pior das três respostas
 * possíveis. Recarregar é honesto, e é o que se espera de um seletor de
 * idioma.
 */
export function EscolhaDoIdioma({
  aoEscolher,
  className,
}: {
  /** Chamado antes de recarregar — serve para fechar a gaveta. */
  aoEscolher?: () => void;
  className?: string;
}) {
  const t = useT();
  const retrato = useRetratoDoIdioma();
  const [gravando, setGravando] = useState(false);

  if (!retrato || retrato.disponiveis.length < 2) return null;

  const trocar = async (codigo: string) => {
    if (codigo === retrato.atual || gravando) return;
    setGravando(true);
    try {
      await api.put(`/a/${encodeURIComponent(retrato.areaSlug)}/idioma`, { idioma: codigo });
      /*
       * A faixa guardada de uma aula anterior deixa de mandar.
       *
       * Sem isto, quem tivesse trocado a faixa para inglês numa aula e depois
       * escolhesse espanhol AQUI continuaria a ouvir inglês — logo a seguir a
       * ter pedido espanhol —, porque a escolha guardada vence a semente. As
       * duas são alterações dele; o que decide é qual foi a última, e foi
       * esta. A regra (e o que ela NÃO toca) está em `aoTrocarALinguaDaArea`.
       */
      guardarPreferencia(aoTrocarALinguaDaArea(preferenciasGuardadas()));
      aoEscolher?.();
      window.location.reload();
    } catch {
      /* A escolha não ficou. Devolver o seletor ao valor do servidor é mais
         honesto do que o deixar a mostrar uma língua que não foi gravada. */
      setGravando(false);
    }
  };

  return (
    <div className={`${className ?? ''} flex-wrap gap-1`} role="group" aria-label={t('nav.idioma')}>
      {retrato.disponiveis.map((i) => {
        const escolhida = i.codigo === retrato.atual;
        return (
          /*
           * O NOME continua aqui, e tem de continuar: o `aria-label` é o que o
           * leitor de tela anuncia, e o `title` é o que o ponteiro revela. O
           * dono tirou o nome da TELA, não do produto — uma bandeira muda
           * seria um controle que só quem já sabe consegue usar.
           *
           * `aria-pressed` e não `aria-selected`: isto é um grupo de botões, e
           * `selected` só tem sentido dentro de um `listbox`.
           */
          <button
            key={i.codigo}
            type="button"
            title={i.nome}
            aria-label={i.nome}
            aria-pressed={escolhida}
            disabled={gravando}
            onClick={() => void trocar(i.codigo)}
            className={`inline-flex min-h-[44px] min-w-[34px] shrink-0 items-center justify-center rounded-md transition-opacity ${
              escolhida ? '' : 'opacity-50 hover:opacity-100'
            }`}
          >
            <Bandeira codigo={i.codigo} />
          </button>
        );
      })}
    </div>
  );
}
